import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A Sankey diagram is a flow diagram used to visualize the flow of resources, energy, or
          information between different states or categories. It consists of arrows or links that
          are proportional in size to the quantity they represent. The arrows originate from one
          category or node and flow towards another, visually showing the distribution and movement
          of quantities. Sankey diagrams are especially useful for illustrating complex processes,
          such as energy usage, financial flows, or the movement of goods, allowing for an easy
          understanding of how different components interact and where the largest flows or losses
          occur.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Sankey diagrams are an excellent tool for visualizing the flow of resources or quantities
          through a system. Here are three key uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Resource Flows:</strong> Ideal for representing resource
            distribution, such as energy consumption, financial transactions, or material usage, and
            identifying areas of inefficiency or waste.
          </li>
          <li>
            <strong>Explaining Complex Processes:</strong> Sankey diagrams help break down intricate
            processes, such as supply chains or user journey pathways, into clear,
            easy-to-understand flows between stages or components.
          </li>
          <li>
            <strong>Highlighting Proportions and Relationships:</strong> The diagram’s weighted
            links make it effective for showcasing proportions, enabling users to understand the
            relative magnitude of flows and the connections between different entities.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Sankey diagrams are a powerful visualization tool for flow data, but they have several key
          limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Complexity with Large Datasets:</strong> When visualizing large or intricate
            datasets, Sankey diagrams can become overly cluttered. Too many nodes and connections
            can make the diagram difficult to interpret, reducing its effectiveness in conveying
            meaningful insights.
          </li>
          <li>
            <strong>Limited Quantitative Precision:</strong> While Sankey diagrams are excellent for
            showing proportions and flow, they are less precise in presenting exact numerical
            values. Users may struggle to discern subtle differences in flow sizes, as the visual
            focus is more on relative comparisons.
          </li>
          <li>
            <strong>Challenging to Scale Dynamically:</strong> Adjusting a Sankey diagram to
            accommodate different screen sizes or varying data dimensions can be challenging.
            Maintaining clarity and usability in responsive designs often requires significant
            customization and manual adjustments.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is vital in Sankey diagrams to clarify the flow and enhance
          interpretability. Using distinct colors for each category or segment helps differentiate
          sources, pathways, and destinations. Gradients can illustrate transitions between nodes,
          highlighting the strength or volume of connections. Careful color choices prevent visual
          overload while drawing attention to key flows, ensuring the diagram is both visually
          engaging and easy to understand.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Sankey diagrams are highly effective for visualizing flows and distributions, but creating
          and interpreting them comes with specific challenges that require attention:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Data Overload and Visual Clutter:</strong> Managing a large dataset in a Sankey
            diagram can lead to overwhelming visual clutter, with numerous overlapping nodes and
            links obscuring essential information. Simplifying or segmenting the data is necessary
            to ensure clarity.
          </li>
          <li>
            <strong>Interpreting Proportional Relationships:</strong> While the width of flows
            represents data proportions, accurately interpreting these relationships can be
            challenging, particularly when dealing with smaller flows or subtle differences in
            width. This may result in miscommunication of data nuances.
          </li>
          <li>
            <strong>Design Complexity for Interactivity:</strong> Creating interactive Sankey
            diagrams for web or software applications can be technically complex. Ensuring smooth
            interactions while maintaining visual fidelity across different platforms often requires
            significant technical expertise and resources.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
